
import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useFollowLinksCache } from "./useFollowLinksCache";
import { useLinkCountsApi, LinkCountsData } from "./useLinkCountsApi";
import { useProcessLinkCounts } from "./useProcessLinkCounts";

export const useFollowLinksCount = (accountIds: string[]) => {
  const [linkCounts, setLinkCounts] = useState<LinkCountsData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const isFirstRender = useRef(true);
  const refreshEventAttached = useRef(false);
  const refreshDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const accountIdsRef = useRef<string[]>([]);
  
  // Import utility hooks
  const { clearCache, getAccountsToFetch, markAccountFetched } = useFollowLinksCache();
  const { fetchFollowLinks, verifyAccountCount } = useLinkCountsApi();
  const { processLinkCounts, updateVerifiedCount } = useProcessLinkCounts();
  
  const fetchLinkCounts = useCallback(async (retryCount = 0, forceFetch = false) => {
    // Return early if no account IDs to fetch
    if (!accountIds.length) return;
    
    // Determine which accounts need to be fetched
    const accountsToFetch = getAccountsToFetch(accountIds, forceFetch);
    
    // Skip if nothing to fetch
    if (accountsToFetch.length === 0) {
      console.log("No new accounts to fetch link counts for");
      return;
    }
    
    console.log(`Fetching link counts for ${accountsToFetch.length} accounts:`, accountsToFetch);
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all follow links for the specified accounts
      const { followLinks, error: fetchError } = await fetchFollowLinks(accountsToFetch);
      
      if (fetchError) throw fetchError;

      // Process the data to count total and used links per account
      let processedCounts = processLinkCounts(followLinks, accountsToFetch, linkCounts);

      // Verify our counts by getting the total count per account
      for (const accountId of accountsToFetch) {
        const { count } = await verifyAccountCount(accountId);
        processedCounts = updateVerifiedCount(accountId, count, processedCounts);
      }
      
      console.log("Final processed link counts:", processedCounts);
      setLinkCounts(processedCounts);
      
      // Mark these accounts as having been fetched
      accountsToFetch.forEach(id => markAccountFetched(id));
      
    } catch (err) {
      console.error("Error fetching follow link counts:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      
      // Implement retry logic with exponential backoff
      if (retryCount < 3) {
        const retryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Retrying in ${retryDelay}ms... (attempt ${retryCount + 1})`);
        
        setTimeout(() => {
          fetchLinkCounts(retryCount + 1, forceFetch);
        }, retryDelay);
      } else {
        // After all retries failed, show a toast
        toast({
          title: "Error loading follow links",
          description: "Could not load follow link counts. Please try refreshing the page.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [accountIds, linkCounts, toast, fetchFollowLinks, verifyAccountCount, processLinkCounts, updateVerifiedCount, getAccountsToFetch, markAccountFetched]);

  // Refresh function with debouncing
  const triggerRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshDebounceTimer.current) {
      clearTimeout(refreshDebounceTimer.current);
    }
    
    // Set a new timer to delay the refresh
    refreshDebounceTimer.current = setTimeout(() => {
      console.log("Debounced refresh triggered");
      fetchLinkCounts(0, true); // Force fetch on manual refresh
      refreshDebounceTimer.current = null;
    }, 300); // 300ms debounce
  }, [fetchLinkCounts]);

  // Run when accountIds change
  useEffect(() => {
    // Skip first render to prevent double fetching
    if (isFirstRender.current) {
      isFirstRender.current = false;
      accountIdsRef.current = accountIds;
      return;
    }

    // Check if the accountIds have actually changed
    const hasNewAccounts = accountIds.some(id => !accountIdsRef.current.includes(id));
    
    if (hasNewAccounts) {
      console.log(`New accounts detected: ${accountIds.filter(id => !accountIdsRef.current.includes(id)).join(',')}`);
      accountIdsRef.current = accountIds;
      
      // Only fetch if we have account IDs
      if (accountIds.length > 0) {
        // Only fetch accounts that haven't been fetched yet
        fetchLinkCounts();
      } else {
        console.log("No account IDs provided, skipping fetch");
        setLinkCounts({});
      }
    } else {
      console.log("No new accounts detected, skipping unnecessary fetch");
    }
    
    // Cleanup debounce timer on unmount
    return () => {
      if (refreshDebounceTimer.current) {
        clearTimeout(refreshDebounceTimer.current);
      }
    };
  }, [fetchLinkCounts, accountIds]);

  // Set up refresh event listener only once
  useEffect(() => {
    if (refreshEventAttached.current) return;
    
    console.log("Setting up event listener for refresh-link-counts");
    const handleRefresh = () => {
      console.log("Manual refresh of link counts triggered");
      triggerRefresh();
    };
    
    window.addEventListener('refresh-link-counts', handleRefresh);
    refreshEventAttached.current = true;
    
    return () => {
      console.log("Cleaning up event listener for refresh-link-counts");
      window.removeEventListener('refresh-link-counts', handleRefresh);
      refreshEventAttached.current = false;
    };
  }, [triggerRefresh]);

  return { 
    linkCounts, 
    isLoading, 
    error, 
    refetch: () => fetchLinkCounts(0, true),
    clearCache 
  };
};
